import { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import {
  CalendarDays, ChevronLeft, ChevronRight, Loader2, Users, Clock, X
} from 'lucide-react';

const JOURS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const MOIS_FR = ['Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aou', 'Sep', 'Oct', 'Nov', 'Dec'];
const HOURS = [];
for (let h = 11; h <= 22; h++) HOURS.push(h);

const STATUS_COLORS = {
  confirmed: 'bg-green-500/20 border-green-500/30 text-green-700',
  pending: 'bg-yellow-500/20 border-yellow-500/30 text-yellow-700',
  canceled: 'bg-red-500/10 border-red-500/20 text-red-400 line-through opacity-50',
};

const AdminCalendar = () => {
  const today = new Date();
  const [weekOffset, setWeekOffset] = useState(0);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedCell, setSelectedCell] = useState(null);

  // Get monday of current week + offset
  const weekDays = useMemo(() => {
    const d = new Date(today);
    const dayOfWeek = (d.getDay() + 6) % 7; // 0=Mon
    d.setDate(d.getDate() - dayOfWeek + weekOffset * 7);
    const days = [];
    for (let i = 0; i < 7; i++) {
      const dd = new Date(d);
      dd.setDate(dd.getDate() + i);
      days.push({
        date: dd.toISOString().slice(0, 10),
        day: dd.getDate(),
        label: JOURS[i],
        month: MOIS_FR[dd.getMonth()],
        isToday: dd.toISOString().slice(0, 10) === today.toISOString().slice(0, 10),
      });
    }
    return days;
  }, [weekOffset]);

  const fetchData = useCallback(async () => {
    const start = weekDays[0].date;
    const end = weekDays[6].date;
    try {
      const res = await api.get(`/resa/calendar?start=${start}&end=${end}`);
      setData(res.data.dates || {});
    } catch { setData({}); }
  }, [weekDays]);

  useEffect(() => {
    setLoading(true);
    fetchData().finally(() => setLoading(false));
  }, [fetchData]);

  // Group reservations by hour for each day
  const getResasForCell = (date, hour) => {
    const dayResas = data[date] || [];
    return dayResas.filter(r => {
      const h = parseInt((r.time_slot || '').split(':')[0], 10);
      return h === hour && r.status !== 'canceled';
    });
  };

  const weekLabel = `${weekDays[0].day} ${weekDays[0].month} — ${weekDays[6].day} ${weekDays[6].month}`;

  if (loading) return (
    <div className="flex justify-center py-20">
      <Loader2 className="w-8 h-8 animate-spin text-[rgb(var(--lm-gold))]" />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="admin-calendar">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CalendarDays size={18} className="text-[rgb(var(--lm-gold))]" />
          <h1 className="text-lg font-title text-[rgb(var(--lm-text))]">Calendrier</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setWeekOffset(0)}
            className="px-2 py-1 text-[10px] text-[rgb(var(--lm-gold))] hover:bg-[rgb(var(--lm-gold)/0.05)] rounded-sm transition-colors"
            data-testid="cal-today">
            Aujourd'hui
          </button>
        </div>
      </div>

      {/* Week nav */}
      <div className="flex items-center justify-between">
        <button onClick={() => setWeekOffset(w => w - 1)} className="p-2 hover:bg-[rgb(var(--lm-surface-elevated))] rounded-sm text-[rgb(var(--lm-text-muted))]" data-testid="week-prev">
          <ChevronLeft size={18} />
        </button>
        <h2 className="text-sm font-title text-[rgb(var(--lm-text))]">{weekLabel}</h2>
        <button onClick={() => setWeekOffset(w => w + 1)} className="p-2 hover:bg-[rgb(var(--lm-surface-elevated))] rounded-sm text-[rgb(var(--lm-text-muted))]" data-testid="week-next">
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Grid */}
      <div className="lm-card overflow-x-auto">
        <div className="min-w-[700px]">
          {/* Day headers */}
          <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-[rgb(var(--lm-border)/0.3)]">
            <div className="p-2" />
            {weekDays.map(d => {
              const dayResas = data[d.date] || [];
              const activeResas = dayResas.filter(r => r.status !== 'canceled');
              const totalCouverts = activeResas.reduce((s, r) => s + (r.guests_count || 0), 0);
              return (
                <div key={d.date}
                  className={`p-2 text-center border-l border-[rgb(var(--lm-border)/0.2)] ${d.isToday ? 'bg-[rgb(var(--lm-gold)/0.05)]' : ''}`}>
                  <p className="text-[9px] tracking-wider uppercase text-[rgb(var(--lm-text-muted))]">{d.label.slice(0, 3)}</p>
                  <p className={`text-lg font-mono ${d.isToday ? 'text-[rgb(var(--lm-gold))] font-bold' : 'text-[rgb(var(--lm-text))]'}`}>{d.day}</p>
                  {activeResas.length > 0 && (
                    <p className="text-[8px] text-[rgb(var(--lm-text-muted))]">{activeResas.length}r &middot; {totalCouverts}c</p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Hour rows */}
          {HOURS.map(hour => (
            <div key={hour} className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-[rgb(var(--lm-border)/0.1)] min-h-[48px]">
              <div className="p-2 text-right pr-3">
                <span className="text-[10px] font-mono text-[rgb(var(--lm-text-muted))]">{hour}:00</span>
              </div>
              {weekDays.map(d => {
                const resas = getResasForCell(d.date, hour);
                return (
                  <div key={`${d.date}-${hour}`}
                    className={`border-l border-[rgb(var(--lm-border)/0.1)] p-0.5 ${d.isToday ? 'bg-[rgb(var(--lm-gold)/0.02)]' : ''}`}>
                    {resas.map(r => {
                      const cfg = STATUS_COLORS[r.status] || STATUS_COLORS.pending;
                      return (
                        <button key={r.id}
                          onClick={() => setSelectedCell(r)}
                          className={`w-full text-left px-1.5 py-1 mb-0.5 text-[9px] border rounded-sm truncate transition-all hover:opacity-80 ${cfg}`}
                          data-testid={`cal-resa-${r.id}`}>
                          <span className="font-medium">{r.time_slot}</span> {r.guest_name} ({r.guests_count})
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Detail popup */}
      {selectedCell && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={() => setSelectedCell(null)}>
          <div className="lm-card w-full max-w-sm mx-4 p-5" onClick={e => e.stopPropagation()} data-testid="cal-detail">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-title text-[rgb(var(--lm-text))]">{selectedCell.guest_name}</h3>
              <button onClick={() => setSelectedCell(null)} className="text-[rgb(var(--lm-text-muted))]"><X size={16} /></button>
            </div>
            <div className="space-y-2 text-sm text-[rgb(var(--lm-text-secondary))]">
              <p className="flex items-center gap-2"><CalendarDays size={13} className="text-[rgb(var(--lm-gold))]" /> {selectedCell.date}</p>
              <p className="flex items-center gap-2"><Clock size={13} className="text-[rgb(var(--lm-gold))]" /> {selectedCell.time_slot}</p>
              <p className="flex items-center gap-2"><Users size={13} className="text-[rgb(var(--lm-gold))]" /> {selectedCell.guests_count} convives</p>
              {selectedCell.zone && <p className="text-xs text-[rgb(var(--lm-text-muted))]">Zone: {selectedCell.zone}</p>}
              {selectedCell.table_name && <p className="text-xs text-[rgb(var(--lm-text-muted))]">Table: {selectedCell.table_name}</p>}
              {selectedCell.guest_phone && <p className="text-xs text-[rgb(var(--lm-text-muted))]">Tel: {selectedCell.guest_phone}</p>}
              {selectedCell.special_requests && (
                <p className="text-xs text-[rgb(var(--lm-text-muted))] italic border-t border-[rgb(var(--lm-border)/0.3)] pt-2 mt-2">
                  "{selectedCell.special_requests}"
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCalendar;
